// "A new version is available" toast.
//
// The service worker no longer calls skipWaiting() on install (see sw.js), so a
// freshly-deployed build parks in the `waiting` state instead of swapping in
// silently. This watcher notices that waiting worker and shows a small,
// persistent toast inviting the user to refresh at a moment of their choosing —
// a short, deliberate interruption rather than a surprise reload mid-stream.
//
// Refreshing reloads onto the new build and reconnects the peer automatically:
// the URL hash is preserved, so a parent reconnects to the same host and the
// host reclaims its persisted id.

import { track, EVENTS } from './analytics.js';
import { openToast, closeToast } from './utils.js';

// Only recheck for a new build this often when the tab regains focus. Baby
// monitors stay open for hours, so a periodic nudge is what surfaces updates
// for them — but we don't want to hammer the server on every tab switch.
const UPDATE_CHECK_INTERVAL = 30 * 60 * 1000; // 30 min

// Guards the single reload triggered by the user tapping Refresh. Multiple tabs
// (or a doubled controllerchange) must reload exactly once.
let refreshing = false;

function hideInstallBanner() {
  // The update toast shares the slot just above the toolbar with the host's
  // install banner; hide the banner so they never stack. No need to restore it
  // — the user is about to reload.
  const banner = document.getElementById('install-banner');
  if (banner) banner.classList.remove('show');
}

function applyUpdate(registration) {
  const waiting = registration.waiting;
  if (!waiting) return;
  // Attach the reload handler ONLY now, on the explicit refresh. Attaching it at
  // init would reload first-time visitors, whose very first SW fires
  // controllerchange via clients.claim().
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (refreshing) return;
    refreshing = true;
    window.location.reload();
  });
  track(EVENTS.APP_UPDATE_APPLIED);
  waiting.postMessage({ type: 'SKIP_WAITING' });
}

let toastShown = false;

function showToast(registration) {
  const el = document.getElementById('update-toast');
  if (!el || toastShown) return;
  toastShown = true;

  hideInstallBanner();

  const refreshBtn = el.querySelector('.update-action');
  const dismissBtn = el.querySelector('.update-dismiss');

  if (refreshBtn) {
    refreshBtn.addEventListener('click', () => applyUpdate(registration));
  }
  if (dismissBtn) {
    // Allow the toast to reappear if another update arrives this session.
    dismissBtn.addEventListener('click', () => closeToast(el, () => { toastShown = false; }));
  }

  openToast(el);
  track(EVENTS.APP_UPDATE_AVAILABLE);
}

// Watch a service-worker registration for an incoming build and surface the
// toast when one is genuinely available (i.e. an update, not a first install).
export function initUpdateWatcher(registration) {
  if (!registration) return;

  // A build may have finished installing during a previous visit and be waiting
  // already when this page loads.
  if (registration.waiting && navigator.serviceWorker.controller) {
    showToast(registration);
  }

  registration.addEventListener('updatefound', () => {
    const installing = registration.installing;
    if (!installing) return;
    installing.addEventListener('statechange', () => {
      // `installed` + an existing controller means this replaces a running SW —
      // a real update. On the very first install there's no controller, so we
      // stay quiet.
      if (installing.state === 'installed' && navigator.serviceWorker.controller) {
        showToast(registration);
      }
    });
  });

  // Long-lived tabs (the baby monitor) may not navigate for hours, so nudge the
  // browser to recheck when the tab comes back to the foreground, throttled.
  let lastCheck = Date.now();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    const now = Date.now();
    if (now - lastCheck < UPDATE_CHECK_INTERVAL) return;
    lastCheck = now;
    registration.update().catch(() => {});
  });
}
